import { useNavigate } from '@tanstack/react-router';
import { useCallback, useMemo, type ReactNode } from 'react';
import type { ScheduleDraftInput } from '../appService';
import { formatDateKey, formatScheduleMonthKey, getScheduleDateKey } from '../scheduleView';
import { WORKSPACE_TABS, type ScheduleMode, type WorkspaceTab } from '../workspaceRoutes';
import type { WorkspaceRouteAdapter } from './appTypes';
import { WorkspaceRouteAdapterContext, useRouteRestorationController } from './context';

export function WorkspaceRouteAdapterProvider({ children }: { readonly children: ReactNode }): ReactNode {
  const navigate = useNavigate();
  const { capturePushRestoration, clearRestorationSnapshots } = useRouteRestorationController();
  const pushWithRestoration = useCallback(
    (push: () => void) => {
      capturePushRestoration();
      push();
    },
    [capturePushRestoration]
  );
  const replaceMainTab = useCallback(
    (tab: WorkspaceTab) => {
      clearRestorationSnapshots();
      void navigate({
        to: '/workspace/$tab',
        params: { tab },
        search: {},
        replace: true,
        resetScroll: false
      });
    },
    [clearRestorationSnapshots, navigate]
  );
  const replaceScheduleRoot = useCallback(
    (input: { readonly mode: ScheduleMode; readonly monthKey: string; readonly date?: string }) => {
      void navigate({
        to: '/workspace/$tab',
        params: { tab: 'schedule' },
        search: {
          mode: input.mode,
          month: input.monthKey,
          date: input.mode === 'calendar' ? input.date : undefined
        },
        replace: true,
        resetScroll: false
      });
    },
    [navigate]
  );
  const openScheduleDetail = useCallback(
    (scheduleId: string) => {
      pushWithRestoration(() => {
        void navigate({
          to: '/workspace/schedules/$scheduleId',
          params: { scheduleId },
          resetScroll: true
        });
      });
    },
    [navigate, pushWithRestoration]
  );
  const openScheduleCreate = useCallback(
    (date: string) => {
      pushWithRestoration(() => {
        void navigate({
          to: '/workspace/schedules/new',
          search: { date },
          resetScroll: true
        });
      });
    },
    [navigate, pushWithRestoration]
  );
  const openScheduleEdit = useCallback(
    (scheduleId: string) => {
      pushWithRestoration(() => {
        void navigate({
          to: '/workspace/schedules/$scheduleId/edit',
          params: { scheduleId },
          resetScroll: true
        });
      });
    },
    [navigate, pushWithRestoration]
  );
  const openScheduleDelete = useCallback(
    (scheduleId: string) => {
      pushWithRestoration(() => {
        void navigate({
          to: '/workspace/schedules/$scheduleId/delete',
          params: { scheduleId },
          resetScroll: true
        });
      });
    },
    [navigate, pushWithRestoration]
  );
  const openScheduleCreateTime = useCallback(() => {
    pushWithRestoration(() => {
      void navigate({
        to: '/workspace/schedules/new/time',
        resetScroll: true
      });
    });
  }, [navigate, pushWithRestoration]);
  const openScheduleEditTime = useCallback(
    (scheduleId: string) => {
      pushWithRestoration(() => {
        void navigate({
          to: '/workspace/schedules/$scheduleId/edit/time',
          params: { scheduleId },
          resetScroll: true
        });
      });
    },
    [navigate, pushWithRestoration]
  );
  const openScheduleCreateAttendance = useCallback(() => {
    pushWithRestoration(() => {
      void navigate({
        to: '/workspace/schedules/new/attendance',
        resetScroll: true
      });
    });
  }, [navigate, pushWithRestoration]);
  const openScheduleEditAttendance = useCallback(
    (scheduleId: string) => {
      pushWithRestoration(() => {
        void navigate({
          to: '/workspace/schedules/$scheduleId/edit/attendance',
          params: { scheduleId },
          resetScroll: true
        });
      });
    },
    [navigate, pushWithRestoration]
  );
  const replaceScheduleDetail = useCallback(
    (scheduleId: string) => {
      void navigate({
        to: '/workspace/schedules/$scheduleId',
        params: { scheduleId },
        replace: true,
        resetScroll: true
      });
    },
    [navigate]
  );
  const replaceScheduleCreate = useCallback(
    (draft: ScheduleDraftInput) => {
      const selectedDate = getScheduleDateKey(draft.startsAt);
      void navigate({
        to: '/workspace/schedules/new',
        search: { date: selectedDate },
        replace: true,
        resetScroll: true
      });
    },
    [navigate]
  );
  const replaceScheduleEdit = useCallback(
    (scheduleId: string) => {
      void navigate({
        to: '/workspace/schedules/$scheduleId/edit',
        params: { scheduleId },
        replace: true,
        resetScroll: true
      });
    },
    [navigate]
  );
  const replaceScheduleCalendar = useCallback(
    (date?: string) => {
      const monthKey = formatScheduleMonthKey(date ?? formatDateKey(new Date()));
      void navigate({
        to: '/workspace/$tab',
        params: { tab: 'schedule' },
        search: {
          mode: 'calendar',
          month: monthKey,
          date
        },
        replace: true,
        resetScroll: true
      });
    },
    [navigate]
  );
  const backToScheduleRoot = useCallback(
    () =>
      navigate({
        to: '/workspace/$tab',
        params: { tab: 'schedule' },
        search: {},
        replace: true,
        resetScroll: true
      }),
    [navigate]
  );
  const openAssignmentDetail = useCallback(
    (assignmentId: string) => {
      pushWithRestoration(() => {
        void navigate({
          to: '/workspace/assignments/$assignmentId',
          params: { assignmentId },
          resetScroll: true
        });
      });
    },
    [navigate, pushWithRestoration]
  );
  const openAssignmentCreate = useCallback(() => {
    pushWithRestoration(() => {
      void navigate({
        to: '/workspace/assignments/new',
        resetScroll: true
      });
    });
  }, [navigate, pushWithRestoration]);
  const openAssignmentSubmission = useCallback(
    (assignmentId: string) => {
      pushWithRestoration(() => {
        void navigate({
          to: '/workspace/assignments/$assignmentId/submission',
          params: { assignmentId },
          resetScroll: true
        });
      });
    },
    [navigate, pushWithRestoration]
  );
  const replaceAssignmentDetail = useCallback(
    (assignmentId: string) => {
      void navigate({
        to: '/workspace/assignments/$assignmentId',
        params: { assignmentId },
        replace: true,
        resetScroll: true
      });
    },
    [navigate]
  );
  const backToAssignments = useCallback(() => {
    void navigate({
      to: '/workspace/$tab',
      params: { tab: 'assignments' },
      replace: true,
      resetScroll: true
    });
  }, [navigate]);
  const backToAssignmentDetail = useCallback(
    (assignmentId: string) => {
      void navigate({
        to: '/workspace/assignments/$assignmentId',
        params: { assignmentId },
        replace: true,
        resetScroll: true
      });
    },
    [navigate]
  );
  const openNotifications = useCallback(() => {
    pushWithRestoration(() => {
      void navigate({
        to: '/workspace/notifications',
        resetScroll: true
      });
    });
  }, [navigate, pushWithRestoration]);
  const openSongDetail = useCallback(
    (songId: string) => {
      pushWithRestoration(() => {
        void navigate({
          to: '/workspace/resources/songs/$songId',
          params: { songId },
          resetScroll: true
        });
      });
    },
    [navigate, pushWithRestoration]
  );
  const openSongRelatedFolder = useCallback(
    (songId: string, folderId: string) => {
      pushWithRestoration(() => {
        void navigate({
          to: '/workspace/resources/songs/$songId/folders/$folderId',
          params: { songId, folderId },
          resetScroll: true
        });
      });
    },
    [navigate, pushWithRestoration]
  );
  const openResourceFile = useCallback(
    (resourceFileId: string) => {
      pushWithRestoration(() => {
        void navigate({
          to: '/workspace/resources/files/$resourceFileId',
          params: { resourceFileId },
          resetScroll: true
        });
      });
    },
    [navigate, pushWithRestoration]
  );
  const backToResources = useCallback(() => {
    void navigate({
      to: '/workspace/$tab',
      params: { tab: 'resources' },
      replace: true,
      resetScroll: true
    });
  }, [navigate]);
  const backToSongDetail = useCallback(
    (songId: string) => {
      void navigate({
        to: '/workspace/resources/songs/$songId',
        params: { songId },
        replace: true,
        resetScroll: true
      });
    },
    [navigate]
  );
  const openChoirManagement = useCallback(() => {
    pushWithRestoration(() => {
      void navigate({
        to: '/workspace/manage/choir',
        resetScroll: true
      });
    });
  }, [navigate, pushWithRestoration]);
  const openMemberGroups = useCallback(() => {
    pushWithRestoration(() => {
      void navigate({
        to: '/workspace/member-groups',
        resetScroll: true
      });
    });
  }, [navigate, pushWithRestoration]);
  const openCreateMemberGroup = useCallback(() => {
    pushWithRestoration(() => {
      void navigate({ to: '/workspace/member-groups/new', resetScroll: true });
    });
  }, [navigate, pushWithRestoration]);
  const openPermissionGroups = useCallback(() => {
    pushWithRestoration(() => {
      void navigate({
        to: '/workspace/permission-groups',
        resetScroll: true
      });
    });
  }, [navigate, pushWithRestoration]);
  const openCreatePermissionGroup = useCallback(() => {
    pushWithRestoration(() => {
      void navigate({ to: '/workspace/permission-groups/new', resetScroll: true });
    });
  }, [navigate, pushWithRestoration]);
  const openMemberGroupDetail = useCallback(
    (memberGroupId: string) => {
      pushWithRestoration(() => {
        void navigate({
          to: '/workspace/member-groups/$memberGroupId',
          params: { memberGroupId },
          resetScroll: true
        });
      });
    },
    [navigate, pushWithRestoration]
  );
  const openRenameMemberGroup = useCallback(
    (memberGroupId: string) => {
      pushWithRestoration(() => {
        void navigate({
          to: '/workspace/member-groups/$memberGroupId/rename',
          params: { memberGroupId },
          resetScroll: true
        });
      });
    },
    [navigate, pushWithRestoration]
  );
  const openDeleteMemberGroup = useCallback(
    (memberGroupId: string) => {
      pushWithRestoration(() => {
        void navigate({
          to: '/workspace/member-groups/$memberGroupId/delete',
          params: { memberGroupId },
          resetScroll: true
        });
      });
    },
    [navigate, pushWithRestoration]
  );
  const backToMemberSettings = useCallback(() => {
    void navigate({
      to: '/workspace/settings/members',
      replace: true,
      resetScroll: true
    });
  }, [navigate]);
  const openApprovalDetail = useCallback(
    (joinRequestId: string) => {
      pushWithRestoration(() => {
        void navigate({
          to: '/workspace/manage/approvals/$joinRequestId',
          params: { joinRequestId },
          resetScroll: true
        });
      });
    },
    [navigate, pushWithRestoration]
  );
  const adapter = useMemo<WorkspaceRouteAdapter>(
    () => ({
      replaceMainTab,
      replaceScheduleRoot,
      openScheduleDetail,
      openScheduleCreate,
      openScheduleEdit,
      openScheduleDelete,
      openScheduleCreateTime,
      openScheduleEditTime,
      openScheduleCreateAttendance,
      openScheduleEditAttendance,
      replaceScheduleDetail,
      replaceScheduleCreate,
      replaceScheduleEdit,
      replaceScheduleCalendar,
      backToScheduleRoot,
      openAssignmentDetail,
      openAssignmentCreate,
      openAssignmentSubmission,
      replaceAssignmentDetail,
      backToAssignments,
      backToAssignmentDetail,
      openNotifications,
      openSongDetail,
      openSongRelatedFolder,
      openResourceFile,
      backToResources,
      backToSongDetail,
      openChoirManagement,
      openMemberGroups,
      openCreateMemberGroup,
      openPermissionGroups,
      openCreatePermissionGroup,
      openMemberGroupDetail,
      openRenameMemberGroup,
      openDeleteMemberGroup,
      backToMemberSettings,
      openApprovalDetail
    }),
    [
      openApprovalDetail,
      backToMemberSettings,
      backToAssignmentDetail,
      backToAssignments,
      backToResources,
      backToSongDetail,
      backToScheduleRoot,
      openAssignmentCreate,
      openAssignmentDetail,
      openAssignmentSubmission,
      replaceAssignmentDetail,
      openChoirManagement,
      openNotifications,
      openCreateMemberGroup,
      openCreatePermissionGroup,
      openDeleteMemberGroup,
      openResourceFile,
      openMemberGroupDetail,
      openMemberGroups,
      openPermissionGroups,
      openRenameMemberGroup,
      openSongDetail,
      openSongRelatedFolder,
      openScheduleCreate,
      openScheduleCreateAttendance,
      openScheduleCreateTime,
      openScheduleDelete,
      openScheduleDetail,
      openScheduleEdit,
      openScheduleEditAttendance,
      openScheduleEditTime,
      replaceScheduleRoot,
      replaceScheduleCalendar,
      replaceScheduleCreate,
      replaceScheduleDetail,
      replaceScheduleEdit,
      replaceMainTab
    ]
  );

  return <WorkspaceRouteAdapterContext.Provider value={adapter}>{children}</WorkspaceRouteAdapterContext.Provider>;
}

export function isWorkspaceTab(value: unknown): value is WorkspaceTab {
  return typeof value === 'string' && WORKSPACE_TABS.includes(value as WorkspaceTab);
}
